import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import _get from 'lodash/get';
import { Draft } from 'immer';

/* Types */
import {
  AddItemSkuProps,
  AddSkuAnswerChangeProps,
  AddSkuDeviceAnswerChangeProps,
  AddSkuDeviceInputAnswerChangeProps,
  AddSkuQuantityChangeProps,
  AddSkuStatePayload,
  AddSkuUpdateSkuQuestionsProps,
} from './store.types';
import { SelectedSkuQuestions, Sku } from 'types/sku.types';
import { QuestionTypes, CustomDropdownValues } from 'types/qa.types';

/* Constants | Methods  */
function updateInitialState(sku: Sku, questions: SelectedSkuQuestions) {
  return {
    questions: questions || [],
    skuId: sku.id || 0,
    autoApplyCoupon: sku.autoApplyCoupon || false,
    startsAtPrice: sku.startsAtPrice || '',
    totalPrice: '',
    overrideBasePrice: sku.overrideBasePrice || false,
    heroImageUrl: sku.heroImageUrl || '',
    category: sku.category || '',
    partner: sku.partner || null,
    name: sku.name || '',
    updated: 'question',
  };
}

type AddSkuStore = AddSkuStatePayload & {
  actions: {
    updateAddSkuAnswer: (payload: AddSkuAnswerChangeProps) => void;
    updateAddSkuQuantity: (payload: AddSkuQuantityChangeProps) => void;
    updateAddSkuAddItem: (payload: AddItemSkuProps) => void;
    updateAddSkuQuestions: (payload: AddSkuUpdateSkuQuestionsProps) => void;
    updateAddSkuDeviceAnswer: (payload: AddSkuDeviceAnswerChangeProps) => void;
    updateAddSkuDeviceInputAnswer: (payload: AddSkuDeviceInputAnswerChangeProps) => void;
  };
};

type DynamicPlanPriceStore = {
  dynamicPlanSavings: string;
  actions: {
    updateDynamicPlanSavings: (display: string) => void;
    resetDynamicPlanSavings: () => void;
  };
};

/* --------------------- ADD SKU STATE  -------------------------------- */
const useAddSkuStore = createWithEqualityFn<AddSkuStore>()(
  immer((set, get) => ({
    ...updateInitialState({} as Sku, []),

    actions: {
      updateAddSkuAnswer: ({ question, value }: AddSkuAnswerChangeProps) => {
        set(state => {
          const updated = `questions.${question.id}-${value}`;
          switch (question.inputType) {
            case QuestionTypes.Input:
              state.questions[question.id].text = value;
              state.updated = '';
              break;
            case QuestionTypes.Textarea:
              state.questions[question.id].text = value;
              state.updated = '';
              break;
            case QuestionTypes.Dropdown:
              state.questions[question.id] = value ? { id: value } : null;
              state.updated = updated;
              break;
            case QuestionTypes.Checkbox: {
              // Get current checkbox states or default to an empty array if none.
              const currentCheckboxState = state.questions[question.id] || [];
              // Create a map from the current states for direct ID-based access.
              // Add each checkbox state to the map with its ID as the key.
              const currentCheckboxMap = currentCheckboxState.reduce((map: Draft<{ [key: number]: any }>, item: Draft<any>) => {
                map[item.id] = item;
                return map;
              }, {});

              // Update states based on the selection; keep existing states where possible.
              // Retain the whole state for existing selections, or create a new one.
              const newCheckboxState = (value as number[]).map(v => currentCheckboxMap[v] || { id: v });

              // Save the new states back to the corresponding question.
              state.questions[question.id] = newCheckboxState;
              state.updated = updated;
              break;
            }
            default: {
              throw new Error(`Unknown inputType: ${question.inputType}`);
            }
          }
        });
      },
      updateAddSkuQuantity: ({ question, id, quantity }: AddSkuQuantityChangeProps) =>
        set(state => {
          const answerIndex = state.questions[question.id].findIndex((a: { id: number }) => a.id === id);

          state.questions[question.id][answerIndex].quantity = quantity;
        }),
      updateAddSkuAddItem: (payload: AddItemSkuProps) =>
        set(state => {
          const { sku = {}, questions = [] } = payload;
          const newQuestions: Record<string, unknown> = {};
          questions.forEach(q => {
            switch (q.inputType) {
              case QuestionTypes.Input:
                newQuestions[q.id.toString()] = { text: '' };
                break;
              case QuestionTypes.Textarea:
                newQuestions[q.id.toString()] = { text: '' };
                break;
              case QuestionTypes.Dropdown: {
                const defaultAnswer = q.answers.find(a => a.default);
                newQuestions[q.id.toString()] = defaultAnswer ? { id: defaultAnswer.id } : null;
                break;
              }
              case QuestionTypes.Checkbox:
                newQuestions[q.id.toString()] = null;
                break;
              case QuestionTypes.Device: {
                const defaultAnswer = q.answers.find(a => a.default);
                newQuestions[q.id.toString()] = defaultAnswer ? { id: defaultAnswer.id } : null;
                break;
              }
              default: {
                throw new Error(`Unknown inputType: ${q.inputType}`);
              }
            }
          });

          return { ...get(), ...updateInitialState(sku as Sku, newQuestions) };
        }),
      updateAddSkuQuestions: ({ sku, questions, breakdown }: AddSkuUpdateSkuQuestionsProps) =>
        set(state => ({ ...get(), ...updateInitialState(sku as Sku, questions), ...(breakdown && breakdown) })),
      updateAddSkuDeviceAnswer: ({ questionType, question, makeValue, modelValue }: AddSkuDeviceAnswerChangeProps) =>
        set(state => {
          const answerMatch = question.answers.find(a => a?.id === makeValue);
          /*
            BE needs the id of the productQuestion, not the answer id.
            But when editing answer, FE needs answer id.
          */
          let makeVal = _get(answerMatch, 'productQuestion.id', CustomDropdownValues.zero);
          let modelVal = modelValue;
          let answerId = makeValue;
          /*
            If answerMatch is not present, it means its either "other" or "idk" option
            The "other" option opens an inputfield which is addressed in "DEVICE_INPUT_ANSWER_CHANGE"
          */
          if (makeValue === CustomDropdownValues.negOne) {
            answerId = null;
            makeVal = CustomDropdownValues.negOne;
            modelVal = CustomDropdownValues.negOne;
          }
          const data = { answerId, answerValue: makeValue, make: makeVal, model: modelVal };
          const values = makeValue || modelValue ? data : null;
          switch (questionType) {
            case 'makeDropdown': {
              state.questions[question.id] = values;
              break;
            }
            case 'modelDropdown': {
              state.questions[question.id].model = modelVal;
              break;
            }
            default:
            // nothing
          }
        }),
      updateAddSkuDeviceInputAnswer: ({ questionType, question, makeInputValue, modelInputValue }: AddSkuDeviceInputAnswerChangeProps) =>
        set(state => {
          const data = { answerId: null, answerValue: makeInputValue, make: makeInputValue, model: modelInputValue };
          const values = makeInputValue || modelInputValue ? data : null;

          switch (questionType) {
            case 'makeInput':
              state.questions[question.id] = values;
              break;
            case 'modelInput':
              state.questions[question.id].model = modelInputValue;
              break;
            default:
            // nothing
          }
        }),
    },
  })),
);

export default useAddSkuStore;

export const useDynamicPlanStore = createWithEqualityFn<DynamicPlanPriceStore>()(
  immer((set, get) => ({
    dynamicPlanSavings: '',
    actions: {
      updateDynamicPlanSavings: (display: string) =>
        set(state => {
          state.dynamicPlanSavings = display ? display.replace('-', '') : state.dynamicPlanSavings || '';
        }),
      resetDynamicPlanSavings: () =>
        set(state => {
          state.dynamicPlanSavings = '';
        }),
    },
  })),
);
